/**
 * Template registry — recipes the composer can pre-fill.
 *
 * Focused on three channels the product is actually built to win, plus the two
 * that map to how it makes money:
 *
 * - **Music video** — the MV pipeline is real (15s one-shot on self-hosted H3,
 *   120s beat-cut on the render worker). Every recipe here is worded to route
 *   to a specific edit-grammar skill the Director already ships, so the prompt
 *   is a trigger rather than a wish: `beat_locked_pop`, `slow_cut_ballad`,
 *   `escalation_ratchet`, `associative_rhyme_montage`, `neon_night_red_amber`,
 *   `silhouette`. Wording that drifts from those `use_when` lines routes to the
 *   generic path and the recipe stops being a recipe.
 * - **Glamour** — fashion/beauty/swimwear editorial.
 * - **Video editing** — the Opus Clip / Chatcut surface. These differ in kind
 *   from the rest: they transform footage the user already has, so they carry
 *   `sourceKind: 'footage'` and the composer must ask for an upload rather
 *   than just seeding a prompt.
 *
 * - **Singing Pet** (AI UGC) — the photo-sings-a-song trend. Like the editing
 *   recipes it needs the user's own material, but a still rather than footage,
 *   so it carries `sourceKind: 'photo'`. Its prompt routes to the Director's
 *   `singing_pet` skill (the `singing_portrait` node on MiniMax-H3).
 *
 * Channels dropped in this pass (corporate, local-services, explainer,
 * ecommerce, film): coverage without pull. A gallery of half-populated
 * verticals reads as unfinished; five populated ones read as a product.
 */
import type { AspectRatio } from '@ponpon/api-client'

export type TemplateChannel =
  | 'music-video'
  | 'glamour'
  | 'video-editing'
  | 'ad-video'
  | 'ai-ugc-video'

export interface TemplateChannelMeta {
  id: TemplateChannel
  label: string
  description: string
}

export const TEMPLATE_CHANNELS: TemplateChannelMeta[] = [
  { id: 'music-video',  label: 'Music Video',   description: 'Cut to a track — beat-locked, ballad, or building' },
  { id: 'glamour',      label: 'Glamour',       description: 'Fashion, beauty and swimwear editorial' },
  { id: 'video-editing', label: 'Video Editing', description: 'Turn footage you already have into short-form' },
  { id: 'ad-video',     label: 'Ad Video',      description: 'Paid creatives with hooks for performance marketing' },
  { id: 'ai-ugc-video', label: 'AI UGC',        description: 'Authentic-feeling user-generated style' },
]

/**
 * Where a recipe's raw material comes from.
 *
 * `prompt` recipes generate from text alone. `footage` recipes are edits of
 * something the user supplies — the prompt describes the *treatment*, and
 * without an upload there is nothing to treat, so the composer must collect
 * one before the run means anything. `photo` recipes are the same contract
 * with a still image: the prompt animates *the user's* picture (a pet that
 * sings), so generating a stand-in subject would miss the point entirely.
 */
export type TemplateSourceKind = 'prompt' | 'footage' | 'photo'

/**
 * The upload a source kind needs before the composer will run it, or null
 * when the prompt alone is enough. Expressed as the same `UploadKind` the
 * upload pipeline assigns a file, so "has the user attached the right thing"
 * is one comparison against `uploadKind(file)`.
 */
export function requiredUploadKind(
  sourceKind: TemplateSourceKind | undefined,
): 'image' | 'video' | null {
  switch (sourceKind ?? 'prompt') {
    case 'footage':
      return 'video'
    case 'photo':
      return 'image'
    case 'prompt':
      return null
  }
}

export interface Template {
  id: string
  channel: TemplateChannel
  name: string
  description: string
  prompt: string
  aspectRatio: AspectRatio
  // Fallback plate when no preview has been generated yet. Every template
  // still has one, so a missing preview degrades instead of leaving a hole.
  emoji: string

  /** Defaults to `'prompt'`; editing recipes set `'footage'`, photo-animating ones `'photo'`. */
  sourceKind?: TemplateSourceKind

  /**
   * Still frame shown in the grid. Poster-first is deliberate: both reference
   * implementations do the same. Sunra's template gallery is 30 images and
   * zero <video>, and Higgsfield's 54-tile preset grid has no <video> and no
   * mp4 in the HTML even after hover — they reserve video for a handful of
   * hero cards. Autoplaying every clip would cost seconds of load and a lot of
   * mobile data to show something the user has not asked to watch.
   */
  posterImage?: string

  /**
   * Loaded and played muted on hover / focus only. This is the actual output
   * of `prompt` through our own pipeline (Seedance 2.0, 5s, 9:16), not stock
   * footage — a preview that promises something the recipe cannot produce is
   * worse than no preview. Re-encoded to 480-wide and stripped of audio, so
   * the whole set is a few MB rather than thirty.
   */
  previewVideo?: string
}

/** Preview asset paths follow from the id, so they cannot drift out of sync. */
const preview = (id: string) => ({
  posterImage: `/previews/${id}.jpg`,
  previewVideo: `/previews/${id}.mp4`,
})

export const TEMPLATES: Template[] = [
  // ---------------------------------------------------------------- Music
  {
    id: 'mv-beat-locked-pop',
    channel: 'music-video',
    name: 'Beat-Locked Pop',
    description: 'Cuts land on the drops — runs, downbeat stabs, quantized ladders.',
    // Worded to hit the `beat_locked_pop` skill's use_when line.
    prompt: 'Cut this to the beat — I want it to land on the drops. High-energy pop performance, bold saturated colour, sharp confident movement in a bright studio cyclorama.',
    aspectRatio: '9:16',
    emoji: '🥁',
    ...preview('mv-beat-locked-pop'),
  },
  {
    id: 'mv-slow-cut-ballad',
    channel: 'music-video',
    name: 'Slow-Cut Ballad',
    description: 'Long quiet holds that decelerate into the climax.',
    prompt: 'My song is a slow ballad — cut it long and quiet, building to the end. A lone singer at an upright piano in a dim room, one window of pale daylight, muted desaturated palette.',
    aspectRatio: '9:16',
    emoji: '🕯️',
    ...preview('mv-slow-cut-ballad'),
  },
  {
    id: 'mv-escalation-ratchet',
    channel: 'music-video',
    name: 'Escalation Ratchet',
    description: 'The edit ramps and never lets up — density climbing to the end.',
    prompt: 'Cut my track so it keeps building and never lets up until the end. A dancer in an empty concrete warehouse, hard shafts of light, movement accelerating from a slow sway into violent spins.',
    aspectRatio: '9:16',
    emoji: '📈',
    ...preview('mv-escalation-ratchet'),
  },
  {
    id: 'mv-associative-rhyme',
    channel: 'music-video',
    name: 'Associative Rhyme Montage',
    description: 'Unrelated places bought one cut at a time with a visual rhyme.',
    prompt: 'Make a music video that jumps between totally unrelated places but still feels connected. Rhyme the cuts on a shared red prop and matched motion — a scarf falling through empty sky, the same red caught on a rain-wet railing.',
    aspectRatio: '9:16',
    emoji: '🔗',
    ...preview('mv-associative-rhyme'),
  },
  {
    id: 'mv-neon-night-red-amber',
    channel: 'music-video',
    name: 'Neon Night — Red & Amber',
    description: 'Sodium-night neon measured red-amber, not magenta-cyan.',
    prompt: 'A sodium-night neon music video, red and amber not magenta and cyan. A singer under a street lamp on a wet night street, absolute black in the shadows, wide slow halation on every practical light.',
    aspectRatio: '9:16',
    emoji: '🌆',
    ...preview('mv-neon-night-red-amber'),
  },
  {
    id: 'mv-silhouette-dance',
    channel: 'music-video',
    name: 'Silhouette',
    description: 'Backlit form falling to solid black against a luminous field.',
    prompt: 'A silhouette music video — a dancer backlit by a huge luminous window, exposed for the background so the body falls to solid black. Two-tone frames, form carried entirely by the outline, haze in the light.',
    aspectRatio: '9:16',
    emoji: '🩰',
    ...preview('mv-silhouette-dance'),
  },

  // -------------------------------------------------------------- Glamour
  // The grammar here is deliberately AI-influencer, not editorial fashion:
  // one woman addressing the lens, an ordinary interior, everyday fitted
  // clothes, flat natural light, phone-camera framing. That is what the
  // reference clips in this category actually look like, and it is a different
  // product from a surf campaign or a couture runway. It also generates more
  // reliably — a prompt that leads with a location gets you the location and
  // no subject, which is exactly how the first poolside attempt came back as
  // an empty pool.
  {
    id: 'gl-mirror-outfit-check',
    channel: 'glamour',
    name: 'Mirror Outfit Check',
    description: 'Full-length mirror, outfit reveal, small turns to camera.',
    prompt: 'A young woman doing an outfit check in a full-length mirror in a bright apartment hallway, wearing a fitted crop top and light-wash jeans. She turns slightly to check the fit, adjusts the hem, and smiles at her reflection. Phone held at chest height, natural indoor daylight, full-body vertical framing.',
    aspectRatio: '9:16',
    emoji: '🪞',
    ...preview('gl-mirror-outfit-check'),
  },
  {
    id: 'gl-hotel-hallway-walk',
    channel: 'glamour',
    name: 'Hotel Hallway Walk',
    description: 'Confident walk straight toward the lens down a warm corridor.',
    prompt: 'A young woman walking confidently toward the camera down a warm wood-panelled hotel corridor, wearing a fitted red co-ord set and carrying a small handbag. Relaxed stride, eye contact with the lens, soft practical corridor lighting, full-body vertical framing.',
    aspectRatio: '9:16',
    emoji: '🚪',
    ...preview('gl-hotel-hallway-walk'),
  },
  {
    id: 'gl-golden-hour-bedroom',
    channel: 'glamour',
    name: 'Golden Hour Bedroom',
    description: 'Warm low window light, direct address, unhurried.',
    prompt: 'A young woman sitting on the edge of a bed beside a window at golden hour, wearing a fitted knit top, warm low sunlight raking across the room. She looks into the camera and tucks her hair behind her ear. Soft natural light, shallow depth of field, vertical mid-shot.',
    aspectRatio: '9:16',
    emoji: '🌇',
    ...preview('gl-golden-hour-bedroom'),
  },
  {
    id: 'gl-poolside-glamour',
    channel: 'glamour',
    name: 'Poolside',
    description: 'Subject-led resort shot — the pool is the backdrop, not the subject.',
    prompt: 'A young woman in stylish swimwear standing at the edge of a turquoise pool, turning to face the camera and pushing her hair back. Bright midday sun, water reflections moving across her, palm shadows on the deck behind. Full-body vertical framing, subject centred and in focus.',
    aspectRatio: '9:16',
    emoji: '🏝️',
    ...preview('gl-poolside-glamour'),
  },

  // --------------------------------------------------------- Video editing
  {
    id: 've-podcast-to-clips',
    channel: 'video-editing',
    name: 'Podcast → Clips',
    description: 'Find the moments that stand alone and cut them out.',
    prompt: 'Take my podcast recording and pull out the strongest self-contained moments as vertical short-form clips. Cut each one to start on the hook, keep both speakers framed, and end before the energy drops.',
    aspectRatio: '9:16',
    emoji: '🎙️',
    sourceKind: 'footage',
    ...preview('ve-podcast-to-clips'),
  },
  {
    id: 've-longform-to-shorts',
    channel: 'video-editing',
    name: 'Long-form → Shorts',
    description: 'One long upload becomes a week of vertical posts.',
    prompt: 'Turn my long-form video into a set of vertical shorts. Reframe to 9:16 keeping the speaker centred, trim the dead air, and open each clip on the most arresting line.',
    aspectRatio: '9:16',
    emoji: '✂️',
    sourceKind: 'footage',
    ...preview('ve-longform-to-shorts'),
  },
  {
    id: 've-talking-head-captions',
    channel: 'video-editing',
    name: 'Talking Head + Captions',
    description: 'Burned-in captions, tightened pauses, clean framing.',
    prompt: 'Clean up my talking-head footage: tighten the pauses and filler words, keep the framing steady, and burn in bold readable captions timed to the speech.',
    aspectRatio: '9:16',
    emoji: '💬',
    sourceKind: 'footage',
    ...preview('ve-talking-head-captions'),
  },
  {
    id: 've-beat-synced-montage',
    channel: 'video-editing',
    name: 'Beat-Synced Montage',
    description: 'Your own clips, cut to a track with real edit grammar.',
    prompt: 'Cut my footage into a montage synced to this track. Hold the wide establishing shots longer, tighten as the section builds, and land the hardest cuts on the downbeats.',
    aspectRatio: '9:16',
    emoji: '🎞️',
    sourceKind: 'footage',
    ...preview('ve-beat-synced-montage'),
  },

  // ------------------------------------------------------------- Ad video
  {
    id: 'ad-product-hook',
    channel: 'ad-video',
    name: 'Product Hook (3 sec)',
    description: 'Hooks the viewer in the first 3 seconds — built for paid social.',
    prompt: 'A close-up of a product being unboxed. The packaging opens dramatically, revealing the item with a satisfying reveal motion. Bold text overlay teases the benefit. Energetic background music cue.',
    aspectRatio: '9:16',
    emoji: '🎁',
    ...preview('ad-product-hook'),
  },
  {
    id: 'ad-before-after',
    channel: 'ad-video',
    name: 'Before / After Split',
    description: 'Classic comparison ad — a hard vertical divide down the centre.',
    prompt: 'A split-screen comparison: left side shows the old / problem state in muted desaturated tones; right side shows the new / solution state in vibrant colour. Animated transition wipe between them. Concise text overlay.',
    aspectRatio: '9:16',
    emoji: '⚖️',
    ...preview('ad-before-after'),
  },

  // ----------------------------------------------------------------- UGC
  {
    id: 'ugc-honest-review',
    channel: 'ai-ugc-video',
    name: 'Honest Review Vibe',
    description: 'Conversational, handheld, authentic — looks user-shot.',
    prompt: 'A relatable person filming themselves with a smartphone, holding the camera at arm\'s length. Casual home setting in the background. They\'re showing off a product / sharing a tip with natural hand gestures. Slight handheld camera shake. Natural daylight.',
    aspectRatio: '9:16',
    emoji: '📱',
    ...preview('ugc-honest-review'),
  },
  {
    id: 'ugc-get-ready-with-me',
    channel: 'ai-ugc-video',
    name: 'Get Ready With Me',
    description: 'Mirror-front routine narrated to camera — the highest-volume UGC format.',
    prompt: 'A person getting ready in front of a bathroom mirror, talking to the camera between steps of their routine. Warm vanity lighting, phone propped against the mirror, casual unscripted energy, natural jump-cut rhythm.',
    aspectRatio: '9:16',
    emoji: '💄',
    ...preview('ugc-get-ready-with-me'),
  },
  {
    id: 'ugc-problem-solution',
    channel: 'ai-ugc-video',
    name: 'Problem → Solution',
    description: 'Open on the frustration, land on the fix — the workhorse UGC ad beat.',
    prompt: 'A person in a kitchen visibly frustrated by an everyday annoyance, then the same person moments later solving it easily and reacting with relief. Handheld phone camera, natural home lighting, candid unpolished delivery.',
    aspectRatio: '9:16',
    emoji: '💡',
    ...preview('ugc-problem-solution'),
  },
  {
    // The viral photo-sings trend. Worded to hit the Director's `singing_pet`
    // skill ("singing pet" / "sing"): the photo is used as-is on the
    // singing_portrait node and the song is generated from the lyrics, so no
    // character sheet or text-to-video stand-in gets made. No preview yet —
    // the emoji plate is the tile until one is generated from this recipe.
    id: 'ugc-singing-pet',
    channel: 'ai-ugc-video',
    name: 'Singing Pet',
    description: 'Drop in a photo of your pet and it sings and dances to an original song — about a minute, lip-synced.',
    prompt: 'Make the pet in my photo sing and dance to an original song — singing pet, about 60 seconds. Use my photo as-is; write fun, catchy lyrics about them (their name, their favourite snack, their best trick) with a verse and a chorus, and have them sing it straight to camera with big expressive mouth movements while dancing to an upbeat pop beat.',
    aspectRatio: '9:16',
    emoji: '🐶🎤',
    sourceKind: 'photo',
  },
]

export function templatesByChannel(channel: TemplateChannel | 'all'): Template[] {
  if (channel === 'all') return TEMPLATES
  return TEMPLATES.filter((t) => t.channel === channel)
}
